/**
 * `<CacheSelector>` — the dropdown UI subcomponent of FormCacheSelector.
 *
 * Architecture §9.8 / UX §5.2 States 3-5 / §5.3 Component Composition.
 *
 * Composition:
 *   - shadcn `Popover` (D-UX-001 — Kit has no combobox primitive).
 *   - shadcn `Command` for accessible keyboard navigation + free-text search.
 *   - lucide-react `Check` and `ChevronDown` icons.
 *
 * Behavior:
 *   - "Sin filtros" pseudo-option always appears first (FR-FFCS-012). Its
 *     `value` is the reserved sentinel `__no_filter__` (R-002) — a string that
 *     can never collide with a user-chosen cache name.
 *   - Named caches are sorted alphabetically (case-insensitive, locale-aware)
 *     via `sortCaches`.
 *   - The active item shows a `Check` icon as prefix; inactive items reserve
 *     equivalent left padding so layout stays aligned.
 *   - `aria-expanded` on the trigger button (AC-009).
 *   - `aria-selected` on each `CommandItem` (AC-010).
 *
 * The actual side effects (GetById, safeParse, onSelected, UpdateLastInUse)
 * live in the parent hook's `handleSelectCache` / `handleSelectNoFilter` —
 * this component is a pure UI shell that forwards user clicks.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../lib/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../lib/ui/command';
import { sortCaches } from '../FormCacheSelector.utils';
import type { FormCacheDTO } from '../FormCacheSelector.types';

/** Reserved CommandItem value for the "Sin filtros" pseudo-option (R-002). */
const NO_FILTER_VALUE = '__no_filter__';

export interface CacheSelectorProps {
  /** Caches to display in the dropdown — already deduplicated by the hook. */
  caches: FormCacheDTO[];
  /** Currently-selected cache id, or `null` when "Sin filtros" is active. */
  activeId: string | null;
  /** Click handler for a named cache item. May reject; errors are swallowed. */
  onSelectCache: (cache: FormCacheDTO) => Promise<void>;
  /** Click handler for the "Sin filtros" pseudo-option. */
  onSelectNoFilter: () => void;
  /** Localized label for the "Sin filtros" pseudo-option. */
  noFilterLabel: string;
}

export function CacheSelector({
  caches,
  activeId,
  onSelectCache,
  onSelectNoFilter,
  noFilterLabel,
}: CacheSelectorProps) {
  const { t } = useTranslation('form-cache-selector');
  const [open, setOpen] = useState(false);

  const sorted = sortCaches(caches);
  const activeName =
    caches.find((c) => c.id === activeId)?.name ?? noFilterLabel;

  const handleSelectCacheClick = (cache: FormCacheDTO) => {
    // Fire-and-swallow: the parent handler may reject (e.g. GetById 404).
    // That failure is reported via the error banner, not by re-throwing into
    // React's render loop.
    void onSelectCache(cache).catch(() => {
      /* parent handler is responsible for surfacing this error */
    });
    setOpen(false);
  };

  const handleSelectNoFilterClick = () => {
    onSelectNoFilter();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          data-testid="cache-selector-trigger"
          className="inline-flex h-9 items-center justify-between gap-2 px-3 border border-gray-300 rounded-md text-sm w-full bg-white hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1"
        >
          <span>{activeName}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <Command>
          <CommandInput
            placeholder={t('selector.searchPlaceholder')}
            data-testid="cache-selector-search"
          />
          <CommandList>
            <CommandEmpty>{t('selector.noResults')}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={NO_FILTER_VALUE}
                data-testid="cache-item-no-filter"
                onSelect={handleSelectNoFilterClick}
                aria-selected={activeId === null}
              >
                {activeId === null ? (
                  <Check data-testid="check-icon-no-filter" />
                ) : (
                  <span className="inline-block w-4" aria-hidden="true" />
                )}
                <span>{noFilterLabel}</span>
              </CommandItem>
              {sorted.map((cache) => (
                <CommandItem
                  key={cache.id}
                  value={cache.name}
                  data-testid={`cache-item-${cache.id}`}
                  onSelect={() => handleSelectCacheClick(cache)}
                  aria-selected={activeId === cache.id}
                >
                  {activeId === cache.id ? (
                    <Check data-testid={`check-icon-${cache.id}`} />
                  ) : (
                    <span className="inline-block w-4" aria-hidden="true" />
                  )}
                  <span>{cache.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
